/**
 * Notification Type → Email Template Type Mapping
 * 
 * Ez a konfiguráció határozza meg, hogy melyik értesítés típushoz
 * melyik email template típust használjuk.
 */

import type { Notification } from '../types/notification';
import type { EmailTemplate } from '../types/emailTemplate';

export const NOTIFICATION_TO_TEMPLATE_MAP: Record<Notification['type'], EmailTemplate['type']> = {
  // User notifications
  'user_invited': 'userInvitation',
  'user_updated': 'notification',
  
  // Task notifications
  'task_assigned': 'taskAssigned',
  'task_completed': 'taskCompleted',
  'task_overdue': 'taskOverdue',
  'task_status_changed': 'notification',
  
  // Project notifications
  'project_created': 'projectCreated',
  'project_updated': 'projectUpdated',
  'project_status_changed': 'notification',
  'project_update': 'notification', // Legacy
  
  // Form notifications
  'form_submitted': 'formSubmitted',
  
  // Appointment notifications
  'appointment_created': 'appointmentCreated',
  'appointment_updated': 'appointmentUpdated',
  
  // Customer notifications
  'customer': 'notification', // Legacy
  'customer_created': 'notification',
  'customer_updated': 'notification',
  
  // Deal notifications
  'deal': 'notification', // Legacy
  'deal_created': 'notification',
  'deal_updated': 'notification',
  
  // Quotation notifications
  'quotation': 'notification', // Legacy
  'quotation_created': 'notification',
  'quotation_updated': 'notification',
  
  // Contract notifications
  'contract': 'notification', // Legacy
  'contract_created': 'notification',
  'contract_updated': 'notification',
  
  // Organization notifications
  'organization_updated': 'notification',
  
  // System notifications
  'deadline_reminder': 'notification',
  'system': 'notification',
  'mention': 'notification',
};

/**
 * Get email template type for a notification type
 */
export const getEmailTemplateType = (notificationType: Notification['type']): EmailTemplate['type'] => {
  return NOTIFICATION_TO_TEMPLATE_MAP[notificationType] || 'notification';
};

/**
 * Check if a notification type has a dedicated template
 */
export const hasDedicatedTemplate = (notificationType: Notification['type']): boolean => {
  const templateType = NOTIFICATION_TO_TEMPLATE_MAP[notificationType];
  return templateType !== 'notification';
};

/**
 * Get all notification types that use a specific template type
 */
export const getNotificationTypesForTemplate = (templateType: EmailTemplate['type']): Notification['type'][] => {
  return Object.entries(NOTIFICATION_TO_TEMPLATE_MAP)
    .filter(([_, template]) => template === templateType)
    .map(([notificationType]) => notificationType as Notification['type']);
};
